import {
  ApplicationReleaseType,
  applicationId,
  applicationName,
  getAndroidId,
  getInstallReferrerAsync,
  getInstallationTimeAsync,
  getIosApplicationReleaseTypeAsync,
  getIosIdForVendorAsync,
  getIosPushNotificationServiceEnvironmentAsync,
  getLastUpdateTimeAsync,
  nativeApplicationVersion,
  nativeBuildVersion,
} from "expo-application";
import { Body, Icon, useTheme } from "@merit/frontend-components";
import { FlatList, Platform, ScrollView, StyleSheet, View } from "react-native";
import { Header } from "@src/components";
import { useEffect, useState } from "react";
import { useNavigation } from "@src/hooks";
import type { TextStyle, ViewStyle } from "react-native";

const getFriendlyReleaseTypeString = (releaseType: ApplicationReleaseType) => {
  switch (releaseType) {
    case ApplicationReleaseType.SIMULATOR:
      return "simulator";
    case ApplicationReleaseType.ENTERPRISE:
      return "enterprise";
    case ApplicationReleaseType.DEVELOPMENT:
      return "development";
    case ApplicationReleaseType.AD_HOC:
      return "ad hoc";
    case ApplicationReleaseType.APP_STORE:
      return "app store";
    case ApplicationReleaseType.UNKNOWN:
    default:
      return "unknown";
  }
};

export const AppInfo = () => {
  const { theme } = useTheme();
  const styles = StyleSheet.create<{
    readonly container: ViewStyle;
    readonly content: ViewStyle;
    readonly label: TextStyle;
    readonly value: TextStyle;
  }>({
    container: {
      backgroundColor: theme.colors.background.white,
      flex: 1,
    },
    content: {
      padding: theme.spacing.l,
      rowGap: theme.spacing.xxl,
    },
    label: {
      ...theme.fontSizes.l,
      fontWeight: theme.fontWeights.semiBold,
      marginBottom: theme.spacing.s,
    },
    value: {
      ...theme.fontSizes.m,
      paddingLeft: theme.spacing.s,
    },
  });

  const [otherDetails, setOtherDetails] = useState<Record<string, unknown>>({});

  const nav = useNavigation();

  useEffect(() => {
    (async () => {
      const commonDetails = {
        installationTimeAsync: await getInstallationTimeAsync(),
      };
      // eslint-disable-next-line functional/no-let
      let platformSpecificDetails = {};
      switch (Platform.OS) {
        case "android":
          platformSpecificDetails = {
            androidId: getAndroidId(),
            installReferrer: await getInstallReferrerAsync(),
            lastUpdateTime: await getLastUpdateTimeAsync(),
          };
          break;
        case "ios":
          platformSpecificDetails = {
            iosApplicationReleaseType: getFriendlyReleaseTypeString(
              await getIosApplicationReleaseTypeAsync()
            ),
            iosIdForVendor: await getIosIdForVendorAsync(),
            iosPushNotificationServiceEnvironment:
              await getIosPushNotificationServiceEnvironmentAsync(),
          };
          break;
        default:
      }
      setOtherDetails({
        ...commonDetails,
        ...platformSpecificDetails,
      });
    })();
  }, []);

  return (
    <View style={styles.container}>
      <Header
        leftElement={<Icon name="chevronLeftMediumAction" />}
        leftOnPress={() => {
          nav.goBack();
        }}
        title="Application Info"
        titleBold
        titleLevel="3"
      />
      <ScrollView contentContainerStyle={styles.content}>
        <View>
          <FlatList
            data={Object.entries({
              applicationId,
              applicationName,
              nativeApplicationVersion,
              nativeBuildVersion,
              ...otherDetails,
            })}
            renderItem={({ item: [key, value] }) => (
              <View>
                <Body style={styles.label}>{key}</Body>
                <Body style={styles.value}>{JSON.stringify(value, null, 2)}</Body>
              </View>
            )}
            scrollEnabled={false}
          />
        </View>
      </ScrollView>
    </View>
  );
};
